import React, { useMemo, useState } from 'react';
import { GitBranch, Bot, Wrench, Shield, HeartPulse, AlertCircle } from 'lucide-react';

/**
 * Vista Workflow del harness (DT-048): consume harness_turn_graph schema_version=1
 * o reconstruye nodos mínimos desde harness_control / tool_execution / llm_call.
 */
function pickGraphs(events = []) {
  return (events || [])
    .filter((e) => e.event_type === 'harness_turn_graph' && e.payload?.schema_version === 1)
    .map((e) => ({
      ...e.payload,
      timestamp: e.timestamp,
    }));
}

function rebuildFromEvents(events = []) {
  const controls = (events || []).filter((e) => e.event_type === 'harness_control');
  const tools = (events || []).filter(
    (e) =>
      e.event_type === 'tool_execution' ||
      e.event_type === 'workspace_kernel_tool' ||
      e.event_type === 'kb_investigation_tool'
  );
  if (!controls.length && !tools.length) return [];

  const nodes = [];
  const edges = [];
  let last = null;
  const push = (node, edgeType = 'next') => {
    nodes.push(node);
    if (last) edges.push({ from: last, to: node.id, type: edgeType });
    last = node.id;
  };

  controls.forEach((c, i) => {
    const sub = c.payload?.subtype || 'control';
    if (sub === 'action_required_resolved') {
      (c.payload?.actions || []).forEach((a, j) => {
        push({
          id: `rar${i}_${j}`,
          kind: 'action_required',
          label: a.kind,
          reason: a.reason,
        });
      });
    } else {
      push({ id: `rc${i}`, kind: 'control', subtype: sub });
    }
  });

  tools.forEach((t, i) => {
    const tool = t.payload?.tool || t.tool || 'tool';
    push(
      {
        id: `rt${i}`,
        kind: 'tool',
        tool,
        success: t.payload?.success !== false,
        kernel: t.payload?.kernel_ws ? 'ws' : t.payload?.kernel_kb ? 'kb' : 'http',
      },
      'requested'
    );
  });

  return [
    {
      schema_version: 1,
      turn_id: 'reconstructed',
      outcome: 'partial',
      nodes,
      edges,
      action_required: [],
      tools_executed: tools.map((t) => t.payload?.tool).filter(Boolean),
      reconstructed: true,
    },
  ];
}

const KIND_STYLE = {
  action_required: {
    bg: 'bg-amber-50 border-amber-300 text-amber-900',
    Icon: Shield,
  },
  llm_round: {
    bg: 'bg-green-50 border-green-300 text-green-900',
    Icon: Bot,
  },
  tool: {
    bg: 'bg-blue-50 border-blue-300 text-blue-900',
    Icon: Wrench,
  },
  control: {
    bg: 'bg-violet-50 border-violet-300 text-violet-900',
    Icon: GitBranch,
  },
  turn_health: {
    bg: 'bg-teal-50 border-teal-300 text-teal-900',
    Icon: HeartPulse,
  },
};

function nodeTitle(n) {
  if (n.kind === 'action_required') return `Action: ${n.label || n.kind}`;
  if (n.kind === 'llm_round') return `LLM r${n.round ?? '?'} (${n.outcome || n.purpose || ''})`;
  if (n.kind === 'tool') {
    const ok = n.success === false ? 'fail' : 'ok';
    return `${n.tool || 'tool'} [${n.kernel || 'http'}/${ok}]`;
  }
  if (n.kind === 'control') return `Control: ${n.subtype || ''}`;
  if (n.kind === 'turn_health') return `Health: ${n.status || ''}`;
  return n.kind || 'node';
}

export default function AutopsyHarnessWorkflow({ events = [], onSelectNode }) {
  const graphs = useMemo(() => {
    const g = pickGraphs(events);
    if (g.length) return g;
    return rebuildFromEvents(events);
  }, [events]);

  const [selectedTurn, setSelectedTurn] = useState(0);
  const graph = graphs[Math.min(selectedTurn, Math.max(0, graphs.length - 1))];

  if (!graphs.length) {
    return (
      <div className="p-8 text-center text-xs text-zinc-500">
        <AlertCircle className="w-6 h-6 mx-auto mb-2 text-zinc-300" />
        No hay grafo de harness para esta conversación.
        <p className="text-[10px] text-zinc-400 mt-1">
          Aparece tras turnos con AgentRuntime (evento harness_turn_graph o controles).
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="autopsy-harness-workflow">
      {graphs.length > 1 && (
        <div className="flex gap-1 flex-wrap">
          {graphs.map((g, i) => (
            <button
              key={g.turn_id || i}
              type="button"
              onClick={() => setSelectedTurn(i)}
              className={`text-[10px] px-2 py-1 rounded-sm border ${
                i === selectedTurn
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-white text-zinc-600 border-zinc-200'
              }`}
            >
              Turn {(g.turn_id || '').slice(0, 8) || i + 1}
              {g.reconstructed ? ' ~' : ''}
            </button>
          ))}
        </div>
      )}

      <div className="rounded-sm border border-zinc-200 bg-zinc-50/80 px-3 py-2 text-[10px] text-zinc-600 flex flex-wrap gap-3">
        <span>
          <strong>outcome:</strong> {graph.outcome || '—'}
        </span>
        <span>
          <strong>actions:</strong> {graph.actions_required_count ?? (graph.action_required || []).length}
          {' / satisfied '}
          {graph.actions_satisfied_count ?? '—'}
        </span>
        <span>
          <strong>retries:</strong> {graph.prose_retries ?? 0}
        </span>
        <span>
          <strong>tools:</strong> {(graph.tools_executed || []).join(', ') || '—'}
        </span>
        {graph.schema_version && (
          <span>
            <strong>schema:</strong> v{graph.schema_version}
          </span>
        )}
      </div>

      <div className="overflow-x-auto pb-2">
        <div className="flex items-stretch gap-0 min-w-min py-2">
          {(graph.nodes || []).map((n, idx) => {
            const style = KIND_STYLE[n.kind] || KIND_STYLE.control;
            const Icon = style.Icon;
            return (
              <React.Fragment key={n.id || idx}>
                {idx > 0 && (
                  <div className="flex items-center px-1">
                    <div className="w-6 h-px bg-zinc-300" />
                    <div className="w-0 h-0 border-t-4 border-b-4 border-l-4 border-transparent border-l-zinc-400" />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onSelectNode?.(n, graph)}
                  className={`max-w-[160px] text-left rounded-sm border px-2 py-1.5 shadow-sm hover:shadow ${style.bg}`}
                  title={JSON.stringify(n)}
                >
                  <div className="flex items-center gap-1 mb-0.5">
                    <Icon className="w-3 h-3 shrink-0" />
                    <span className="text-[9px] uppercase tracking-wide opacity-70">{n.kind}</span>
                  </div>
                  <div className="text-[10px] font-medium leading-snug break-words">{nodeTitle(n)}</div>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {(graph.action_required || []).length > 0 && (
        <div className="text-[10px] text-zinc-600 border border-zinc-100 rounded-sm p-2 bg-white">
          <strong className="text-zinc-800">ActionRequired inicial:</strong>
          <ul className="mt-1 list-disc pl-4">
            {graph.action_required.map((a, i) => (
              <li key={i}>
                {a.kind} — {a.reason} ({a.source})
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
